/**
 * حذفِ حساب، دسترسیِ «ورود با Apple» را هم پس می‌گیرد (WHOSUP-251).
 *
 * ═══ چرا ═══
 *
 * بندِ 5.1.1(v) راهنمای بازبینیِ اپل: اپی که «ورود با Apple» دارد، موقعِ
 * حذفِ حساب باید توکنِ اپلِ کاربر را با `appleid.apple.com/auth/revoke`
 * باطل کند. بدونِ آن، `delete_my_account` (مایگریشن 0046) داده‌ها را پاک
 * می‌کند ولی Outami هنوز در تنظیماتِ آیفون → Apple ID → «ورود با Apple»
 * فهرست می‌شود، و ورودِ بعدی با Apple برگه‌ی انتخابِ ایمیل/اسم را نشان
 * نمی‌دهد — اپل فکر می‌کند این کاربر هنوز عضوِ ماست.
 *
 * ═══ توکن از کجا می‌آید ═══
 *
 * ورود با Apple از مسیرِ وبِ OAuth در Supabase است (`native/oauth.ts`)، پس
 * کدِ اولیه‌ی اپل هیچ‌وقت به دستِ ما نمی‌رسد؛ Supabase مبادله‌اش می‌کند. ولی
 * درست در لحظه‌ی برگشت، توکن‌هایی که اپل به Supabase داده همراهِ نشست
 * می‌آیند: `provider_token` (دسترسی، یک ساعت) و `provider_refresh_token`
 * (بلندمدت). Supabase آن‌ها را **جایی نگه نمی‌دارد** و با اولین رفرشِ نشست
 * از دست می‌روند. خوشبختانه حذفِ حساب همین حالا هم یک «ورودِ دوباره» درست
 * پیش از قدمِ آخر دارد (`afterAuth.ts`) — پس کاربری که با Apple وارد شده،
 * همان‌جا دوباره با Apple وارد می‌شود و توکنِ تازه چند ثانیه بعد لازم است.
 *
 * این فایل همان توکن را در **حافظه** نگه می‌دارد (نه روی دیسک) و موقعِ حذف
 * به تابعِ `revoke-apple` می‌دهد. کلیدِ امضا و Services ID فقط در secretهای
 * آن تابع‌اند (`scripts/deploy-revoke-apple.mjs`)، هرگز در اپ.
 *
 * ═══ چرا باید بدانیم «این ورود با Apple بود» ═══
 *
 * `provider_token` نمی‌گوید مالِ کدام ارائه‌دهنده است. کاربری که هم Apple
 * دارد هم گوگل، اگر با گوگل دوباره وارد شود، توکنِ گوگل می‌آید. پس هر اپ
 * درست پیش از رفتن به مرورگر `noteOAuthProvider` را می‌زند (روی دیسک، چون
 * وب در این رفت‌وبرگشت کلاً صفحه را عوض می‌کند) و `captureProviderTokens`
 * فقط وقتی توکن را نگه می‌دارد که آن یادداشت `apple` باشد.
 *
 * ═══ ورودِ نیتیو روی آیفون (WHOSUP-306) ═══
 *
 * از وقتی Apple روی iOS از برگه‌ی نیتیو می‌رود (`native/oauth.ts`)، دیگر
 * `provider_token`ی در کار نیست؛ ولی برگه‌ی نیتیو یک `authorizationCode`
 * می‌دهد. آن کد فقط **پنج دقیقه** معتبر است و فقط با `client_id`ِ خودِ
 * بسته‌ی اپ (`com.outami.app`، یا `ca.whosup.dev`) مبادله می‌شود — نه با
 * Services IDِ وب. پس همان کد با شناسه‌ی بسته نگه داشته می‌شود
 * (`holdAppleAuthorizationCode`) و تابعِ `revoke-apple` اول آن را به
 * refresh token تبدیل می‌کند و بعد باطلش می‌کند. پنج دقیقه کم به نظر
 * می‌رسد، ولی این کد هم درست در «ورودِ دوباره»ی پیش از حذف گرفته می‌شود؛
 * اگر کاربر بیشتر معطل کند، همان قاعده‌ی شکستِ پایین: `no-token` و حذف.
 *
 * ═══ قاعده‌ی شکست ═══
 *
 * هیچ‌چیزِ این فایل throw نمی‌کند. اگر پس‌گرفتن نشد — شبکه، اپل، توکن نبود
 * چون کاربر با ایمیل دوباره وارد شد — حذف **ادامه پیدا می‌کند** و فقط
 * `reportError` ثبت می‌شود: پاک شدنِ داده‌های کاربر مهم‌تر از فهرستِ تنظیماتِ
 * آیفون است. ترتیب ولی ثابت است: **اول پس‌گرفتن، بعد حذف** — چون
 * `delete_my_account` نشست را می‌کشد و تابع بدونِ نشست صدازننده را نمی‌شناسد.
 */
import type { User } from '@supabase/supabase-js';
import { storage } from '../platform.ts';
import { reportError } from './report.ts';
import { supabase } from './supabase.ts';
import type { OAuthProvider } from './authProviders.ts';

const PROVIDER_KEY = 'auth.oauthProvider';

/**
 * توکنی که از این کهنه‌تر است دیگر به کار نمی‌آید. توکنِ دسترسیِ اپل یک
 * ساعت عمر دارد؛ و حذفِ حساب بعد از ورودِ دوباره فقط ده دقیقه باز است
 * (`DELETE_REAUTH_TTL_MS`)، پس یک ساعت حاشیه‌ی کافی است.
 */
export const APPLE_TOKEN_TTL_MS = 60 * 60_000;

/** بیشتر از این منتظرِ اپل نمی‌مانیم؛ کاربر جلوی «در حالِ حذف» نشسته. */
export const APPLE_REVOKE_TIMEOUT_MS = 8000;

/** عمرِ `authorizationCode`ِ برگه‌ی نیتیوِ اپل — قاعده‌ی خودِ اپل. */
export const APPLE_CODE_TTL_MS = 5 * 60_000;

/**
 * دو مقداری که `token_type_hint`ِ اپل می‌پذیرد، به‌اضافه‌ی کدِ برگه‌ی نیتیو
 * که تابع پیش از باطل کردن مبادله‌اش می‌کند.
 */
export type AppleTokenType = 'refresh_token' | 'access_token' | 'authorization_code';

export interface AppleToken {
  token: string;
  type: AppleTokenType;
  /** فقط برای `authorization_code`: شناسه‌ی بسته‌ی اپی که کد را گرفت. */
  clientId?: string;
}

/** توکن‌های خامِ ارائه‌دهنده، همان‌طور که از نشست یا لینکِ برگشت می‌آیند. */
export interface ProviderTokens {
  accessToken?: string | null;
  refreshToken?: string | null;
}

/**
 * از دو توکن کدام را بفرستیم: refresh اگر هست.
 *
 * اپل می‌گوید باطل کردنِ refresh_token همه‌ی توکن‌های آن اجازه را باطل
 * می‌کند و اجازه را از فهرستِ کاربر برمی‌دارد؛ access_token فقط خودش.
 */
export function pickAppleToken(t: ProviderTokens): AppleToken | null {
  if (t.refreshToken) return { token: t.refreshToken, type: 'refresh_token' };
  if (t.accessToken) return { token: t.accessToken, type: 'access_token' };
  return null;
}

/** آیا این حساب هویتِ Apple دارد. `identities` اولویت دارد؛ `app_metadata` پشتیبان. */
export function hasAppleIdentity(user: Pick<User, 'identities' | 'app_metadata'> | null | undefined): boolean {
  if (!user) return false;
  if (user.identities?.some((i) => i.provider === 'apple')) return true;
  const meta = user.app_metadata ?? {};
  if (meta.provider === 'apple') return true;
  return Array.isArray(meta.providers) && meta.providers.includes('apple');
}

/* ─────────────────────────── نگه‌داشتنِ توکن ─────────────────────────── */

let held: (AppleToken & { userId: string; at: number }) | null = null;

/** «دارم با این ارائه‌دهنده می‌روم» — درست پیش از باز کردنِ مرورگر. */
export async function noteOAuthProvider(provider: OAuthProvider): Promise<void> {
  try {
    await storage().setItem(PROVIDER_KEY, provider);
  } catch (e) {
    // نرسیدنِ این یادداشت فقط یعنی پس‌گرفتنِ بعدی توکن ندارد؛ ورود نباید بشکند.
    reportError('appleRevoke.note', e);
  }
}

/**
 * توکن‌های ارائه‌دهنده که همین حالا با یک نشست رسیدند.
 *
 * بی‌توکن هیچ کاری نمی‌کند — **حتی یادداشت را هم نمی‌خورد**. این عمدی است:
 * روی موبایل `setSession` اول `SIGNED_IN` را بی‌توکن پخش می‌کند
 * (`session.tsx` این‌جا را صدا می‌زند) و بعد `_layout` با توکن‌های خودِ
 * لینک. اگر اولی یادداشت را می‌خورد، دومی دیگر نمی‌دانست Apple بوده.
 */
export async function captureProviderTokens(
  userId: string | null | undefined, tokens: ProviderTokens,
): Promise<void> {
  const picked = pickAppleToken(tokens);
  if (!userId || !picked) return;
  try {
    const provider = await storage().getItem(PROVIDER_KEY);
    if (!provider) return;
    await storage().removeItem(PROVIDER_KEY);
    if (provider === 'apple') held = { ...picked, userId, at: Date.now() };
  } catch (e) {
    reportError('appleRevoke.capture', e);
  }
}

/**
 * کدِ برگه‌ی نیتیوِ اپل، درست بعد از ورود (WHOSUP-306). یادداشتِ
 * `noteOAuthProvider` لازم نیست: این را فقط مسیرِ Apple صدا می‌زند.
 */
export function holdAppleAuthorizationCode(
  userId: string | null | undefined,
  code: string | null | undefined,
  clientId: string | null | undefined,
  now: number = Date.now(),
): void {
  if (!userId || !code || !clientId) return;
  held = { token: code, type: 'authorization_code', clientId, userId, at: now };
}

/** توکنِ نگه‌داشته برای همین کاربر، اگر هنوز تازه است. یک‌بارمصرف. */
export function takeAppleToken(userId: string, now: number = Date.now()): AppleToken | null {
  const h = held;
  if (!h || h.userId !== userId) return null;
  held = null;
  const ttl = h.type === 'authorization_code' ? APPLE_CODE_TTL_MS : APPLE_TOKEN_TTL_MS;
  if (now < h.at || now - h.at > ttl) return null;
  return h.clientId
    ? { token: h.token, type: h.type, clientId: h.clientId }
    : { token: h.token, type: h.type };
}

/** فقط برای تست. */
export function _resetAppleTokenForTest(): void {
  held = null;
}

/* ─────────────────────────── پس‌گرفتن ─────────────────────────── */

/**
 * نتیجه، برای تست و برای لاگ:
 *   `not-apple` — حساب هویتِ Apple ندارد؛ کاری نبود.
 *   `no-token`  — Apple دارد ولی این ورود با Apple نبود (یا توکن کهنه شد).
 *   `revoked`   — اپل پذیرفت.
 *   `failed`    — تابع یا اپل خطا داد، یا جواب نیامد.
 */
export type AppleRevokeOutcome = 'not-apple' | 'no-token' | 'revoked' | 'failed';

export interface AppleRevokeIo {
  currentUser(): Promise<Pick<User, 'id' | 'identities' | 'app_metadata'> | null>;
  takeToken(userId: string): AppleToken | null;
  /** خطا را پرت می‌کند اگر نشد. */
  revoke(t: AppleToken): Promise<void>;
  report(where: string, e: unknown): void;
  timeoutMs?: number;
}

/** منطقِ خالص — نگاه کن به بالای فایل. هرگز throw نمی‌کند. */
export async function runAppleRevoke(io: AppleRevokeIo): Promise<AppleRevokeOutcome> {
  let user: Awaited<ReturnType<AppleRevokeIo['currentUser']>>;
  try {
    user = await io.currentUser();
  } catch (e) {
    io.report('account.appleRevoke.user', e);
    return 'failed';
  }
  if (!user || !hasAppleIdentity(user)) return 'not-apple';

  const token = io.takeToken(user.id);
  if (!token) {
    // حساب Apple دارد ولی کاربر با راهِ دیگری دوباره وارد شد. حذف ادامه
    // دارد؛ این ردیف فقط می‌گوید چند نفر به این راه افتاده‌اند.
    io.report('account.appleRevoke.noToken', new Error('apple identity without a fresh apple token'));
    return 'no-token';
  }

  const ms = io.timeoutMs ?? APPLE_REVOKE_TIMEOUT_MS;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const call = io.revoke(token);
    // اگر مهلت تمام شد، شکستِ دیرهنگامِ همین درخواست نباید unhandled بماند.
    call.catch(() => {});
    await (ms > 0
      ? Promise.race([call, new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('apple revoke timed out')), ms);
      })])
      : call);
    return 'revoked';
  } catch (e) {
    io.report('account.appleRevoke', e);
    return 'failed';
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** پس‌گرفتنِ واقعی: همان منطق، وصل به نشستِ Supabase و تابعِ `revoke-apple`. */
export async function revokeAppleAccess(): Promise<AppleRevokeOutcome> {
  return runAppleRevoke({
    currentUser: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.user ?? null;
    },
    takeToken: (id) => takeAppleToken(id),
    revoke: async (t) => {
      const { error } = await supabase.functions.invoke('revoke-apple', {
        body: { token: t.token, token_type_hint: t.type, client_id: t.clientId },
      });
      if (error) throw error;
    },
    report: reportError,
  });
}
